import { flickerFactor } from "./frame-light.ts";
import type { Falloff, Flicker } from "./types.ts";

/**
 * A light's flicker and the curves its strength falls along (ADR-141 decisions A4 and D2). Pure and seeded only by their
 * inputs, so the studio's preview, Daedalus and the game show the same flicker.
 */

/**
 * Smoothstep of a value between 0 and 1.
 *
 * @param x A value; clamped to 0 to 1.
 * @returns 3x^2 - 2x^3.
 */
function smoothstep(x: number): number {
  const c = x < 0 ? 0 : x > 1 ? 1 : x;
  return c * c * (3 - 2 * c);
}

/**
 * The strength factor of a flickering light at a time: 1 when steady, dipping toward `1 - amount`. It is smooth value noise
 * over the lattice of `hz` points a second, each point a hash of the seed and its index.
 *
 * @param timeMs Time in milliseconds.
 * @param seed The light's seed, a non-negative integer (its id folded to a number by the caller).
 * @param flicker The preset's flicker.
 * @returns A factor from `1 - amount` to 1.
 */
export function flickerAt(timeMs: number, seed: number, flicker: Flicker): number {
  return flickerFactor(timeMs, seed, flicker.amount, flicker.hz);
}

/**
 * A light's strength at a distance from its centre.
 *
 * @param kind The preset's falloff.
 * @param distance The distance as a share of the radius; 1 and beyond is the edge.
 * @returns 1 at the centre falling to 0 at the edge; `hard` holds 1 until the edge.
 */
export function falloffAt(kind: Falloff, distance: number): number {
  if (distance >= 1) return 0;
  const inside = distance <= 0 ? 0 : distance;
  switch (kind) {
    case "linear":
      return 1 - inside;
    case "smooth":
      return smoothstep(1 - inside);
    case "hard":
      return 1;
  }
}
